import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import AnimatedPage from '@/components/AnimatedPage';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { StatCard } from '@easyflow/ui-master/components/ui/stat-card';
import { Skeleton } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { 
  Building2, 
  Users, 
  Phone, 
  Activity, 
  Plus, 
  Eye, 
  BarChart3,
  Settings,
  LogOut,
  TrendingUp,
  AlertCircle,
  RefreshCw,
  LogIn
} from 'lucide-react';
import { LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import apiClient from '@/lib/api';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import { impersonationService } from '@/services/impersonation';
import { HealthMonitorWidget } from '@/components/widgets/HealthMonitorWidget';
import { QuickActionsWidget } from '@/components/widgets/QuickActionsWidget';
import { TenantHealthWidget } from '@/components/widgets/TenantHealthWidget';
import { PeriodComparisonWidget } from '@/components/widgets/PeriodComparisonWidget';
import { AlertsWidget } from '@/components/widgets/AlertsWidget';
import { RedisHealthWidget } from '@/components/widgets/RedisHealthWidget';
import { resolveTenantOrigin } from '@/lib/productOrigins';
import { PRODUCT_REGISTRY, toErr } from '@easyflow/shared';
import type {
  CrossTenantSummary,
  CrossTenantCallTrendPoint,
  MasterDashboardTenantsResponse,
} from '@easyflow/shared';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-master/components/ui/select';
import { chartLabel } from '@/lib/recharts-format';

// Task #3653 — Contratto dati unico BE↔FE: le shape di RISPOSTA API vivono in
// @easyflow/shared (schemi Zod + z.infer). Qui restano solo i view-model
// normalizzati lato UI (CrossTenantStats, CallTrendData, TenantStats).
interface CrossTenantStats {
  total_tenants: number;
  total_users: number;
  total_extensions: number;
  total_calls_24h: number;
  active_tenants: number;
  inactive_tenants: number;
}

interface CallTrendData {
  date: string;
  calls: number;
  total_duration: number;
  avg_duration: number;
  active_tenants: number;
}

interface TenantStats {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  tenant_product: string | null;
  users_count: number;
  extensions_count: number;
  calls_24h: number;
  calls_7d: number;
  companies_count: number;
  contacts_count: number;
  status: string;
}

export default function SuperAdminDashboard() {
  const queryClient = useQueryClient();
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [impersonationError, setImpersonationError] = useState('');
  const navigate = useNavigate();
  // Task #3266 — segmentazione per prodotto/edition ('all' | key da PRODUCT_REGISTRY).
  const [productFilter, setProductFilter] = useState<string>('all');
  const productQuery = productFilter !== 'all' ? `product=${encodeURIComponent(productFilter)}` : '';

  const { data: stats = null, isLoading: statsLoading, error: statsError } = useQuery<CrossTenantStats | null>({
    queryKey: ['master-dashboard-summary', productFilter],
    queryFn: async () => {
      const res = await apiClient.get<CrossTenantSummary>(`/master/analytics/cross-tenant/summary${productQuery ? `?${productQuery}` : ''}`);
      if (res.success && res.data) {
        const summary = res.data;
        return {
          total_tenants: parseInt(summary.total_tenants) || 0,
          total_users: parseInt(summary.total_users) || 0,
          total_extensions: parseInt(summary.total_extensions) || 0,
          total_calls_24h: parseInt(summary.total_calls) || 0,
          active_tenants: parseInt(summary.active_tenants) || 0,
          inactive_tenants: (parseInt(summary.total_tenants) || 0) - (parseInt(summary.active_tenants) || 0),
        };
      }
      return null;
    },
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });

  const { data: tenantStats = [] } = useQuery<TenantStats[]>({
    queryKey: ['master-dashboard-tenants', productFilter],
    queryFn: async () => {
      const res = await apiClient.get<MasterDashboardTenantsResponse>(`/master/dashboard/tenants${productQuery ? `?${productQuery}` : ''}`);
      if (res.success && res.data) {
        const data = res.data;
        const tenants = data.tenants || [];
        // Task #3653 — i conteggi arrivano già normalizzati a number dal backend.
        return tenants.map((t) => ({
          tenant_id: t.id,
          tenant_name: t.name,
          tenant_slug: t.slug,
          tenant_product: t.product,
          users_count: t.user_count,
          extensions_count: t.extension_count,
          calls_24h: t.calls_24h,
          calls_7d: t.calls_7d,
          companies_count: t.store_count,
          contacts_count: t.contacts_count,
          status: t.status,
        }));
      }
      return [];
    },
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });

  const { data: callTrends = [] } = useQuery<CallTrendData[]>({
    queryKey: ['master-dashboard-trends'],
    queryFn: async () => {
      const res = await apiClient.get<CrossTenantCallTrendPoint[]>('/master/analytics/cross-tenant/calls-trend?period=7d');
      if (res.success && res.data) {
        return res.data.map((t) => ({
          date: t.time_bucket || '',
          calls: Number(t.total_calls) || 0,
          total_duration: Number(t.total_duration) || 0,
          avg_duration: Number(t.avg_duration) || 0,
          active_tenants: 0
        }));
      }
      return [];
    },
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
  });

  const loading = statsLoading && !stats;
  const refreshing = statsLoading && !!stats;
  const error = impersonationError || (statsError ? (statsError).message : '');
  const lastRefresh = new Date();

  const loadDashboardData = (isRefresh = false) => {
    void queryClient.invalidateQueries({ queryKey: ['master-dashboard-summary'] });
    void queryClient.invalidateQueries({ queryKey: ['master-dashboard-tenants'] });
    void queryClient.invalidateQueries({ queryKey: ['master-dashboard-trends'] });
  };

  const handleLogout = () => {
    localStorage.removeItem(AUTH_STORAGE_KEYS.TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER);
    localStorage.removeItem('edgvoip_master_refresh_token');
    apiClient.clearToken();
    navigate('/login');
  };

  const handleImpersonate = async (tenantId: string, tenantSlug: string, tenantProduct?: string | null) => {
    try {
      setImpersonating(tenantId);
      const response = await impersonationService.impersonate(tenantId);
      
      if (response.success && response.data?.token) {
        const tokenData = response.data;
        const masterToken = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
        // Task #3260 — origin product-aware del tenant (fallback piattaforma).
        const tenantOrigin = await resolveTenantOrigin(tenantProduct);
        let tenantUrl = `${tenantOrigin}/${tenantSlug}/auth/impersonate?token=${encodeURIComponent(tokenData.token)}`;
        if (masterToken) {
          tenantUrl += `&backup=${encodeURIComponent(masterToken)}`;
        }
        window.location.href = tenantUrl;
      } else {
        setImpersonationError(response.error || 'Failed to impersonate tenant');
        setImpersonating(null);
      }
    } catch (err: unknown) {
      const __err = toErr(err);
      setImpersonationError(__err.message || 'Failed to impersonate tenant');
      setImpersonating(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-400">Active</Badge>;
      case 'suspended':
        return <Badge variant="destructive" className="bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-400">Suspended</Badge>;
      case 'pending':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-400">Pending</Badge>;
      default:
        return <Badge variant="outline" className="text-foreground dark:text-muted-foreground/60">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="h-8 w-64 bg-muted dark:bg-white/20 rounded animate-pulse mb-2"></div>
          <div className="h-4 w-96 bg-muted rounded animate-pulse"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
        
        <div className="mb-8">
          <Skeleton className="h-48 rounded-lg" />
        </div>
        
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <GlassCard className="w-full max-w-md">
          <GlassCardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Error</h3>
              <p className="text-muted-foreground dark:text-muted-foreground/70 mb-4">{error}</p>
              <Button onClick={() => loadDashboardData(false)} variant="outline">
                Retry
              </Button>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    );
  }

  return (
    <AnimatedPage>
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 dark:from-cyan-400 dark:to-purple-400">
              Management Dashboard
            </h1>
            <p className="text-muted-foreground dark:text-muted-foreground/70 mt-2">Overview of all tenants and system statistics</p>
          </div>
          <div className="flex items-center gap-4">
            <p className="text-sm text-muted-foreground dark:text-muted-foreground">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
            <Select value={productFilter} onValueChange={setProductFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Prodotto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i prodotti</SelectItem>
                {PRODUCT_REGISTRY.map(p => (
                  <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => { e.preventDefault(); loadDashboardData(true); }}
              disabled={refreshing}
              className="border-border dark:border-white/20 text-foreground dark:text-muted-foreground/60"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
        
        {/* Stats Cards with Glassmorphism */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Tenants"
            value={stats?.total_tenants || 0}
            icon={Building2}
            description={`${stats?.active_tenants || 0} active, ${stats?.inactive_tenants || 0} inactive`}
            variant="default"
          />

          <StatCard
            title="Total Users"
            value={stats?.total_users || 0}
            icon={Users}
            description="Across all tenants"
            variant="success"
          />

          <StatCard
            title="Total Extensions"
            value={stats?.total_extensions || 0}
            icon={Phone}
            description="VoIP extensions configured"
            variant="info"
          />

          <StatCard
            title="Calls (24h)"
            value={stats?.total_calls_24h || 0}
            icon={Activity}
            description="Last 24 hours"
            variant="warning"
            trend={(() => {
              const sorted = [...callTrends].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
              if (sorted.length >= 2) {
                const prev = sorted[sorted.length - 2]?.calls ?? 0;
                const curr = sorted[sorted.length - 1]?.calls ?? 0;
                if (prev > 0) {
                  const pct = Math.abs(((curr - prev) / prev) * 100);
                  return { value: Math.round(pct * 10) / 10, isPositive: curr >= prev };
                }
              }
              return undefined;
            })()}
          />
        </div>

        {/* Operational row 1 — Health + Redis */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 mb-6">
          <div className="lg:col-span-8 h-full">
            <HealthMonitorWidget />
          </div>
          <div className="lg:col-span-4 h-full">
            <RedisHealthWidget contentMaxHeightClass="max-h-[calc(100vh-22rem)] overflow-y-auto pr-1" />
          </div>
        </div>

        {/* Operational row 2 — Ops */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 mb-6">
          <div className="lg:col-span-4 h-full">
            <QuickActionsWidget />
          </div>
          <div className="lg:col-span-4 h-full">
            <AlertsWidget />
          </div>
          <div className="lg:col-span-4 h-full">
            <PeriodComparisonWidget />
          </div>
        </div>

        {/* Operational row 3 — Tenant Health */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          <div className="lg:col-span-12">
            <TenantHealthWidget />
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Call Trends Line Chart */}
          <GlassCard className="backdrop-blur-sm bg-white/60 dark:bg-white/5 border-border dark:border-white/20 lg:col-span-2">
            <GlassCardHeader>
              <GlassCardTitle className="text-lg font-semibold text-foreground">Call Trends (7 Days)</GlassCardTitle>
              <GlassCardDescription>Daily call volume and activity</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={callTrends}>
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    className="text-muted-foreground dark:text-muted-foreground/70"
                    stroke="currentColor"
                  />
                  <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground dark:text-muted-foreground/70" stroke="currentColor" />
                  <Tooltip 
                    contentStyle={{ background: 'rgba(255,255,255,0.95)', borderRadius: '8px', border: 'none' }}
                    labelFormatter={(value) => new Date(chartLabel(value)).toLocaleDateString()}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="calls" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Calls"
                  />
                </LineChart>
              </ResponsiveContainer>
            </GlassCardContent>
          </GlassCard>

          {/* Tenant Distribution Pie Chart */}
          <GlassCard className="backdrop-blur-sm bg-white/60 dark:bg-white/5 border-border dark:border-white/20">
            <GlassCardHeader>
              <GlassCardTitle className="text-lg font-semibold text-foreground">Tenant Call Distribution</GlassCardTitle>
              <GlassCardDescription>Calls by tenant (7 days)</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={tenantStats
                      .filter(t => t.calls_7d > 0)
                      .slice(0, 5)
                      .map(t => ({ name: t.tenant_name, value: t.calls_7d }))
                    }
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                  >
                    {tenantStats.filter(t => t.calls_7d > 0).slice(0, 5).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444'][index % 5]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </GlassCardContent>
          </GlassCard>
        </div>

        {/* Navigation Shortcuts */}
        <div className="mb-8">
          <GlassCard className="backdrop-blur-sm bg-white/60 dark:bg-white/5 border-border dark:border-white/20">
            <GlassCardHeader>
              <GlassCardTitle className="text-foreground">Navigation Shortcuts</GlassCardTitle>
              <GlassCardDescription>Common administrative tasks</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button 
                  className="h-24 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all duration-200 hover:scale-105"
                  onClick={() => navigate('/tenants?action=create')}
                >
                  <Plus className="h-8 w-8" />
                  <span className="text-sm font-medium">Create New Tenant</span>
                </Button>
                <Button 
                  variant="outline"
                  className="h-24 flex flex-col items-center justify-center gap-3 border-2 border-border dark:border-white/20 hover:bg-muted text-foreground dark:text-muted-foreground/60 transition-all duration-200 hover:scale-105"
                  onClick={() => navigate('/tenants')}
                >
                  <Eye className="h-8 w-8" />
                  <span className="text-sm font-medium">View All Tenants</span>
                </Button>
                <Button 
                  variant="outline"
                  className="h-24 flex flex-col items-center justify-center gap-3 border-2 border-border dark:border-white/20 hover:bg-muted text-foreground dark:text-muted-foreground/60 transition-all duration-200 hover:scale-105"
                  onClick={() => navigate('/analytics')}
                >
                  <BarChart3 className="h-8 w-8" />
                  <span className="text-sm font-medium">View Analytics</span>
                </Button>
              </div>
            </GlassCardContent>
          </GlassCard>
        </div>

        {/* Tenant Overview */}
        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle className="text-foreground">Tenant Overview</GlassCardTitle>
            <GlassCardDescription>Summary of all tenants in the system</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border dark:border-white/10">
                    <th className="text-left py-3 px-4 text-foreground w-1/4">Tenant</th>
                    <th className="text-center py-3 px-4 text-foreground w-24">Status</th>
                    <th className="text-center py-3 px-4 text-foreground w-20">Users</th>
                    <th className="text-center py-3 px-4 text-foreground w-24">Extensions</th>
                    <th className="text-center py-3 px-4 text-foreground w-24">Companies</th>
                    <th className="text-center py-3 px-4 text-foreground w-24">Calls (7d)</th>
                    <th className="text-center py-3 px-4 text-foreground w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {tenantStats.map((tenant) => (
                    <tr key={tenant.tenant_id} className="border-b border-border dark:border-white/10 hover:bg-muted/50 dark:hover:bg-white/5">
                      <td className="py-3 px-4">
                        <div>
                          <div className="font-medium text-foreground">{tenant.tenant_name}</div>
                          <div className="text-sm text-muted-foreground dark:text-muted-foreground/70">{tenant.tenant_slug}</div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getStatusBadge(tenant.status)}
                      </td>
                      <td className="py-3 px-4 text-center text-foreground dark:text-muted-foreground/60">{tenant.users_count}</td>
                      <td className="py-3 px-4 text-center text-foreground dark:text-muted-foreground/60">{tenant.extensions_count}</td>
                      <td className="py-3 px-4 text-center text-foreground dark:text-muted-foreground/60">{tenant.companies_count}</td>
                      <td className="py-3 px-4 text-center text-foreground dark:text-muted-foreground/60">{tenant.calls_7d}</td>
                      <td className="py-3 px-4 text-center">
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => navigate(`/tenants/${tenant.tenant_id}`)}
                          title="View tenant details"
                          className="h-8 w-8 hover:bg-muted"
                        >
                          <Eye className="h-4 w-4 text-muted-foreground dark:text-muted-foreground/70" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    </AnimatedPage>
  );
}